import { useEffect, useState } from "react";
import { Box, Heading, Spinner, Flex, Select, Text } from "@chakra-ui/react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BACKEND_URL } from "../../../api/config";
import { useDispatch } from "react-redux";
import { getEventsInfo } from "../../../slice/certificateIssuerHomeSlice";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/** Helper component that auto-fits the map bounds to cover all markers */
function FitBounds({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (!locations || locations.length === 0) return;

    const points = locations
      .filter((loc) => loc.latitude != null && loc.longitude != null)
      .map((loc) => [loc.latitude, loc.longitude]);

    if (points.length === 0) return;

    if (points.length === 1) {
      map.setView(points[0], 12);
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [locations, map]);

  return null;
}

/** Compute { startDate, endDate } ISO strings from a dropdown value */
function getDateRange(rangeValue) {
  const now = new Date();
  const endDate = now.toISOString();

  const start = new Date(now);
  switch (rangeValue) {
    case "30d":
      start.setDate(start.getDate() - 30);
      break;
    case "90d":
      start.setDate(start.getDate() - 90);
      break;
    case "7d":
    default:
      start.setDate(start.getDate() - 7);
      break;
  }

  return { startDate: start.toISOString(), endDate };
}

function IssuerScanStats({ productionId }) {
  const dispatch = useDispatch();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d");

  useEffect(() => {
    if (!productionId) return;
    fetchScanLocations();
  }, [productionId, range]);

  useEffect(() => {
    dispatch(getEventsInfo({ page: 1, pageSize: 100 }));
  }, [dispatch]);

  const fetchScanLocations = async () => {
    setLoading(true);

    try {
      const { startDate, endDate } = getDateRange(range);
      const res = await fetch(
        `${BACKEND_URL}/scan-statistics/production/${productionId}/locations?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        {
          credentials: "include",
          cache: "no-store",
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch scan locations", err);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="300px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box
      bg="white"
      boxShadow="lg"
      borderRadius="lg"
      p={6}
      w="100%"
      mb={10}
      border="1px solid #e5e7eb"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md" color="#273B60">
          Scan Locations
        </Heading>

        <Select width="160px" value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 3 Months</option>
        </Select>
      </Flex>

      <Text fontSize="sm" color="gray.600" mb={4}>
        Hover over a location to see scan activity.
      </Text>

      {locations.length === 0 ? (
        <Text color="gray.500">No scan data available.</Text>
      ) : (
        <Box height="500px" borderRadius="md" overflow="hidden">
          <MapContainer center={[20, 0]} zoom={2} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FitBounds locations={locations} />

            {locations.map((loc, idx) => (
              <Marker key={idx} position={[loc.latitude ?? 0, loc.longitude ?? 0]}>
                <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                  <Box>
                    <Text fontWeight="bold">{loc.locationName ?? "Unknown Location"}</Text>
                    <Text fontSize="sm">Scans: {loc.count ?? 0}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {loc.percentage ?? 0}% of total
                    </Text>
                  </Box>
                </Tooltip>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      )}
    </Box>
  );
}

export default IssuerScanStats;
